// src/pages/markets/board/state/boardGroupsContext.ts
//
// The read side of the group config, and deliberately nothing else. Cards, the
// picker and the info panel all name a group through here, so this module must
// stay free of Firestore and react-admin: pulling either into the card tree
// drags react-admin's MUI re-exports into every card's test run.
//
// The value is produced by useBoardGroupsValue (useBoardGroups.ts), which the
// board root calls once and provides.

import { createContext, useContext } from 'react';
import { resolveGroup, type BoardGroup } from './boardGroups';

export interface BoardGroupsApi {
    // Never undefined: a group with no document resolves to its humanized key.
    resolve: (group: string) => BoardGroup;
    // The display name, with the group key appended only for an admin looking
    // at a label collision.
    titleOf: (group: string) => string;
    all: ReadonlyMap<string, BoardGroup>;
    isAdmin: boolean;
    isLoaded: boolean;
    isSuccess: boolean;
    // The read failed. The picker offers everything rather than nothing.
    failOpen: boolean;
    offered: (group: string) => boolean;
    setVisible: (group: string, visible: boolean) => Promise<void>;
    // The three fields an admin writes. Colour, seed column and visibility are
    // deliberately not here: they are set elsewhere and a text edit must not
    // silently carry them along.
    saveText: (group: string, text: BoardGroupText) => Promise<void>;
}

export interface BoardGroupText {
    label: string;
    oneLiner: string;
    description: string;
}

// A provider-less default rather than a thrown error, so a card rendered on its
// own in a test still names its groups. Nothing is curated and nothing is
// hidden, which is the same answer the board gives while the read is in flight.
export const FALLBACK_BOARD_GROUPS: BoardGroupsApi = {
    resolve: group => resolveGroup(group, undefined),
    titleOf: group => resolveGroup(group, undefined).label,
    all: new Map(),
    isAdmin: false,
    isLoaded: false,
    isSuccess: false,
    failOpen: true,
    offered: () => true,
    setVisible: async () => undefined,
    saveText: async () => undefined,
};

export const BoardGroupsContext = createContext<BoardGroupsApi>(
    FALLBACK_BOARD_GROUPS
);

export function useBoardGroups(): BoardGroupsApi {
    return useContext(BoardGroupsContext);
}
